import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  IndianRupee, TrendingUp, Building2, Clock, RefreshCw, Download,
  ArrowUpRight, CreditCard, Receipt, CheckCircle, AlertCircle, Loader2,
  Users, GraduationCap, Phone, Mail, Printer, X, Search, BookOpen, Award, Check, MessageCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CenterFinancePage = () => {
  const [loading, setLoading] = useState(true);
  const [financeData, setFinanceData] = useState({
    totalRevenue: 0,
    centerShare: 0,
    totalDue: 0,
    pendingFees: 0,
    recentTransactions: []
  });
  
  // Tab state - overview, stt, rpl, distance
  const [activeTab, setActiveTab] = useState('overview');
  
  // STT Installment tracking states
  const [installmentData, setInstallmentData] = useState({ students: [], summary: {} });
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const [installmentSearch, setInstallmentSearch] = useState('');
  
  // RPL tracking states
  const [rplData, setRplData] = useState({ students: [], summary: {} });
  const [loadingRpl, setLoadingRpl] = useState(false);
  const [rplSearch, setRplSearch] = useState('');
  
  // Distance Education tracking states
  const [distanceData, setDistanceData] = useState({ students: [], summary: {} });
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [distanceSearch, setDistanceSearch] = useState('');
  
  // Collect Fees states
  const [collectFeesDialogOpen, setCollectFeesDialogOpen] = useState(false);
  const [pendingFeeStudents, setPendingFeeStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [collectAmount, setCollectAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [isCollecting, setIsCollecting] = useState(false);
  
  // Receipt states
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const receiptRef = useRef(null);
  
  // Pay Admission Fees states
  const [payAdmissionDialogOpen, setPayAdmissionDialogOpen] = useState(false);
  const [admissionPendingStudents, setAdmissionPendingStudents] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Center info for receipt
  const [centerInfo, setCenterInfo] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchFinanceData();
    fetchCenterInfo();
    fetchInstallmentData();
    fetchRplData();
    fetchDistanceData();
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  
  // Fetch STT Installment data
  const fetchInstallmentData = async () => {
    setLoadingInstallments(true);
    try {
      const response = await axios.get(`${API}/center/stt-installments`, { headers: getAuthHeaders() });
      setInstallmentData(response.data || { students: [], summary: {} });
    } catch (error) {
      console.error('Failed to load installment data:', error);
      setInstallmentData({ students: [], summary: {} });
    } finally {
      setLoadingInstallments(false);
    }
  };
  
  // Fetch RPL data
  const fetchRplData = async () => {
    setLoadingRpl(true);
    try {
      const response = await axios.get(`${API}/center/rpl-students`, { headers: getAuthHeaders() });
      setRplData(response.data || { students: [], summary: {} });
    } catch (error) {
      console.error('Failed to load RPL data:', error);
      setRplData({ students: [], summary: {} });
    } finally {
      setLoadingRpl(false);
    }
  };
  
  // Fetch Distance Education data
  const fetchDistanceData = async () => {
    setLoadingDistance(true);
    try {
      const response = await axios.get(`${API}/center/distance-education-students`, { headers: getAuthHeaders() });
      setDistanceData(response.data || { students: [], summary: {} });
    } catch (error) {
      console.error('Failed to load Distance Education data:', error);
      setDistanceData({ students: [], summary: {} });
    } finally {
      setLoadingDistance(false);
    }
  };
  
  // Filter installment students
  const filteredInstallmentStudents = installmentData.students?.filter(student => {
    const search = installmentSearch.toLowerCase();
    return (
      student.name?.toLowerCase().includes(search) ||
      student.email?.toLowerCase().includes(search) ||
      student.phone?.includes(search) ||
      student.course_name?.toLowerCase().includes(search)
    );
  }) || [];
  
  // Filter RPL students
  const filteredRplStudents = rplData.students?.filter(student => {
    const search = rplSearch.toLowerCase();
    return (
      student.name?.toLowerCase().includes(search) ||
      student.email?.toLowerCase().includes(search) ||
      student.phone?.includes(search) ||
      student.course_name?.toLowerCase().includes(search)
    );
  }) || [];
  
  // Filter Distance Education students
  const filteredDistanceStudents = distanceData.students?.filter(student => {
    const search = distanceSearch.toLowerCase();
    return (
      student.name?.toLowerCase().includes(search) ||
      student.email?.toLowerCase().includes(search) ||
      student.phone?.includes(search) ||
      student.course_name?.toLowerCase().includes(search)
    );
  }) || [];
  
  // Fetch center info for receipt
  const fetchCenterInfo = async () => {
    try {
      const response = await axios.get(`${API}/center/me`, { headers: getAuthHeaders() });
      setCenterInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch center info:', error);
    }
  };

  const fetchFinanceData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/center/finance-summary`, { headers: getAuthHeaders() });
      setFinanceData({
        ...response.data,
        totalDue: response.data.iqraDue || 0
      });
    } catch (error) {
      setFinanceData({
        totalRevenue: 0,
        centerShare: 0,
        totalDue: 0,
        pendingFees: 0,
        recentTransactions: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch students with pending fees
  const fetchPendingFeeStudents = async () => {
    setLoadingStudents(true);
    try {
      const response = await axios.get(`${API}/center/students`, { headers: getAuthHeaders() });
      const students = response.data || [];
      const pending = students.filter(s => s.fee_status !== 'paid' && s.fee_pending > 0);
      setPendingFeeStudents(pending);
    } catch (error) {
      toast.error('Failed to load students');
      setPendingFeeStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Fetch students for admission fees (IQRA share pending)
  const fetchAdmissionPendingStudents = async () => {
    setLoadingStudents(true);
    try {
      const response = await axios.get(`${API}/center/iqra-pending-students`, { headers: getAuthHeaders() });
      setAdmissionPendingStudents(response.data.students || []);
    } catch (error) {
      toast.error('Failed to load pending students');
      setAdmissionPendingStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const openCollectFeesDialog = () => {
    setSelectedStudent(null);
    setCollectAmount('');
    setPaymentMode('Cash');
    setCollectFeesDialogOpen(true);
    fetchPendingFeeStudents();
  };

  const openPayAdmissionDialog = () => {
    setSelectedStudent(null);
    setPayAdmissionDialogOpen(true);
    fetchAdmissionPendingStudents();
  };

  const selectStudentForCollection = (student) => {
    setSelectedStudent(student);
    // Default to next-due installment amount (1st if pending, else 2nd, else remaining)
    const inst1Pending = (Number(student.installment_1_amount) || 0) - (Number(student.installment_1_paid_amount) || 0);
    const inst2Pending = (Number(student.installment_2_amount) || 0) - (Number(student.installment_2_paid_amount) || 0);
    let suggested = student.fee_pending;
    if (student.installment_1_status !== 'paid' && inst1Pending > 0) {
      suggested = inst1Pending;
    } else if (student.installment_2_status && student.installment_2_status !== 'paid' && student.installment_2_status !== 'na' && inst2Pending > 0) {
      suggested = inst2Pending;
    }
    setCollectAmount(suggested?.toString() || '');
  };

  const handleCollectFees = async () => {
    if (!selectedStudent || !collectAmount) {
      toast.error('Please select student and enter amount');
      return;
    }
    
    const amount = parseFloat(collectAmount);
    if (amount <= 0 || amount > selectedStudent.fee_pending) {
      toast.error('Invalid amount');
      return;
    }
    
    setIsCollecting(true);
    try {
      const response = await axios.post(`${API}/center/collect-fee`, {
        student_id: selectedStudent.id,
        amount: amount,
        payment_mode: paymentMode
      }, { headers: getAuthHeaders() });
      
      // Update local selectedStudent so the dialog reflects new installment statuses immediately
      const apiData = response?.data || {};
      setSelectedStudent(prev => prev ? ({
        ...prev,
        fee_paid: apiData.new_paid ?? ((prev.fee_paid || 0) + amount),
        fee_pending: apiData.new_pending ?? Math.max(0, (prev.fee_pending || 0) - amount),
        installment_1_status: apiData.installment_1_status ?? prev.installment_1_status,
        installment_2_status: apiData.installment_2_status ?? prev.installment_2_status,
        installment_1_paid_amount: apiData.installment_1_paid_amount ?? prev.installment_1_paid_amount,
        installment_2_paid_amount: apiData.installment_2_paid_amount ?? prev.installment_2_paid_amount,
      }) : prev);
      
      // Use authoritative values from server response (post-payment totals)
      const totalFee = Number(selectedStudent.total_fee || selectedStudent.course_fee) || 0;
      const newPaid = Number(apiData.new_paid ?? ((selectedStudent.fee_paid || 0) + amount));
      const newPending = Number(apiData.new_pending ?? Math.max(0, totalFee - newPaid));

      // Generate receipt
      const receipt = {
        receiptNo: apiData.receipt_number || `RCP-${Date.now().toString().slice(-8)}`,
        date: new Date().toLocaleDateString('en-IN'),
        studentName: selectedStudent.name,
        studentId: selectedStudent.student_id,
        phone: selectedStudent.mobile || selectedStudent.phone,
        course: selectedStudent.course_name || selectedStudent.course,
        amountPaid: amount,
        totalPaid: newPaid,
        totalFee: totalFee,
        pendingAfter: newPending,
        paymentMode: paymentMode,
        installment_1_status: apiData.installment_1_status,
        installment_2_status: apiData.installment_2_status,
      };
      
      setReceiptData(receipt);
      setCollectFeesDialogOpen(false);
      setReceiptDialogOpen(true);
      toast.success(`₹${amount.toLocaleString()} collected successfully!`);
      fetchFinanceData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to collect fee');
    } finally {
      setIsCollecting(false);
    }
  };

  const handlePayAdmissionFees = async (student) => {
    if (!student) return;
    
    setSelectedStudent(student);
    setIsProcessing(true);
    
    try {
      const orderResponse = await axios.post(`${API}/center/pay-student-iqra-share`, {
        student_id: student.id
      }, { headers: getAuthHeaders() });
      
      const { order_id, razorpay_key, amount, student: studentInfo, center_name } = orderResponse.data;
      
      const options = {
        key: razorpay_key,
        amount: amount * 100,
        currency: 'INR',
        name: 'Iqra Skills Pvt Ltd',
        description: `Admission Fees - ${studentInfo.name} (${studentInfo.course_name})`,
        order_id: order_id,
        handler: async function (response) {
          try {
            await axios.post(`${API}/center/verify-student-iqra-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              student_id: student.id
            }, { headers: getAuthHeaders() });
            
            toast.success(`₹${amount.toLocaleString()} admission fees paid for ${studentInfo.name}!`);
            setPayAdmissionDialogOpen(false);
            setSelectedStudent(null);
            fetchFinanceData();
            fetchAdmissionPendingStudents();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: { name: center_name },
        theme: { color: '#6b46c1' },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
            setSelectedStudent(null);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setSelectedStudent(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendReceiptWhatsApp = (data) => {
    if (!data) return;
    const phone = (data.phone || '').toString().replace(/\D/g, '');
    if (!phone || phone.length < 10) {
      toast.error('Student ka mobile number nahi mila');
      return;
    }
    // Convert to international format (assume India +91 if 10-digit)
    const intl = phone.length === 10 ? `91${phone}` : phone;
    const centerName = financeData?.center?.center_name || 'Iqra Skills';
    const status = data.pendingAfter <= 0 ? '✅ PAID IN FULL' : '';
    const lines = [
      `*Payment Receipt — ${centerName}*`,
      ``,
      `Receipt No: ${data.receiptNo}`,
      `Date: ${data.date}`,
      ``,
      `Student: ${data.studentName}`,
      `Student ID: ${data.studentId || 'N/A'}`,
      `Course: ${data.course || 'N/A'}`,
      ``,
      `Total Course Fee: ₹${(data.totalFee || 0).toLocaleString()}`,
      `Total Paid: ₹${(data.totalPaid || 0).toLocaleString()}`,
      data.pendingAfter > 0 ? `Remaining Balance: ₹${(data.pendingAfter || 0).toLocaleString()}` : null,
      ``,
      `*Amount Paid: ₹${(data.amountPaid || 0).toLocaleString()}*`,
      `Payment Mode: ${data.paymentMode || 'Cash'}`,
      status ? `\n${status}` : '',
      ``,
      `Thank you for your payment! 🙏`,
    ].filter(Boolean).join('\n');
    const url = `https://wa.me/${intl}?text=${encodeURIComponent(lines)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success('WhatsApp khul raha hai...');
  };

  const handlePrintReceipt = () => {
    const printContent = receiptRef.current;
    if (!printContent) return;
    
    // Create hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    
    const printStyles = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { 
          font-family: 'Inter', Arial, sans-serif; 
          background: white;
          padding: 5mm;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        @media print {
          @page { size: A5 portrait; margin: 8mm; }
          body { padding: 0; }
        }
        img { max-width: 100%; height: auto; }
        .text-emerald-600 { color: #059669 !important; }
        .text-emerald-700 { color: #047857 !important; }
        .text-slate-400 { color: #94a3b8 !important; }
        .text-slate-600 { color: #475569 !important; }
        .text-slate-700 { color: #334155 !important; }
        .text-slate-800 { color: #1e293b !important; }
        .text-amber-600 { color: #d97706 !important; }
        .text-amber-700 { color: #b45309 !important; }
        .bg-emerald-50 { background-color: #ecfdf5 !important; }
        .bg-amber-50 { background-color: #fffbeb !important; }
        .bg-slate-50 { background-color: #f8fafc !important; }
        .bg-slate-100 { background-color: #f1f5f9 !important; }
        .border-emerald-500 { border-color: #10b981 !important; }
        .border-amber-500 { border-color: #f59e0b !important; }
        .border-slate-100 { border-color: #f1f5f9 !important; }
        .border-slate-200 { border-color: #e2e8f0 !important; }
        .border-slate-300 { border-color: #cbd5e1 !important; }
        .border-slate-400 { border-color: #94a3b8 !important; }
        table { border-collapse: collapse; width: 100%; }
        .font-mono { font-family: monospace !important; }
        .font-bold { font-weight: 700 !important; }
        .font-semibold { font-weight: 600 !important; }
        .font-medium { font-weight: 500 !important; }
        .text-xs { font-size: 10px !important; }
        .text-sm { font-size: 12px !important; }
        .text-base { font-size: 14px !important; }
        .text-xl { font-size: 18px !important; }
        .uppercase { text-transform: uppercase !important; }
        .text-center { text-align: center !important; }
        .text-right { text-align: right !important; }
        .text-left { text-align: left !important; }
        .flex { display: flex !important; }
        .grid { display: grid !important; }
        .grid-cols-2 { grid-template-columns: repeat(2, 1fr) !important; }
        .justify-between { justify-content: space-between !important; }
        .items-center { align-items: center !important; }
        .gap-2 { gap: 8px !important; }
        .gap-6 { gap: 24px !important; }
        .p-3 { padding: 12px !important; }
        .p-4 { padding: 16px !important; }
        .px-3 { padding-left: 12px !important; padding-right: 12px !important; }
        .px-4 { padding-left: 16px !important; padding-right: 16px !important; }
        .py-2 { padding-top: 8px !important; padding-bottom: 8px !important; }
        .py-3 { padding-top: 12px !important; padding-bottom: 12px !important; }
        .pt-3 { padding-top: 12px !important; }
        .pt-4 { padding-top: 16px !important; }
        .pb-3 { padding-bottom: 12px !important; }
        .mt-4 { margin-top: 16px !important; }
        .mb-1 { margin-bottom: 4px !important; }
        .space-y-4 > * + * { margin-top: 16px !important; }
        .border { border: 1px solid #e2e8f0 !important; }
        .border-t { border-top: 1px solid #e2e8f0 !important; }
        .border-b { border-bottom: 1px solid #e2e8f0 !important; }
        .border-b-2 { border-bottom: 2px solid #94a3b8 !important; }
        .border-2 { border: 2px solid !important; }
        .border-dashed { border-style: dashed !important; }
        .rounded-lg { border-radius: 8px !important; }
        .rounded-xl { border-radius: 12px !important; }
        .overflow-hidden { overflow: hidden !important; }
        .w-24 { width: 96px !important; }
        .h-10 { height: 40px !important; }
        .h-1\\.5 { height: 6px !important; }
        .inline-block { display: inline-block !important; }
        .tracking-wider { letter-spacing: 0.1em !important; }
        .w-3 { width: 12px !important; }
        .h-3 { height: 12px !important; }
      </style>
    `;
    
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Fee Receipt - Iqra Skills</title>
          ${printStyles}
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Print error:', e);
      }
      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-finance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Finance & Revenue</h1>
          <p className="text-slate-500 mt-1">Track your earnings and payments</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button onClick={fetchFinanceData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Revenue Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Total Revenue</p>
              <p className="text-3xl font-bold mt-2">₹{financeData.totalRevenue.toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-2">
                <ArrowUpRight className="w-4 h-4" />
                <span className="text-sm">+18% this month</span>
              </div>
            </div>
            <IndianRupee className="w-10 h-10 text-emerald-200" />
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-100 text-sm">Center Share (Total)</p>
              <p className="text-3xl font-bold mt-2">₹{financeData.centerShare.toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-2">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Available</span>
              </div>
            </div>
            <TrendingUp className="w-10 h-10 text-purple-200" />
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-white shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-amber-100 text-sm">Total Due</p>
              <p className="text-3xl font-bold mt-2">₹{financeData.totalDue.toLocaleString()}</p>
              <Button 
                size="sm" 
                className="mt-3 bg-white/20 hover:bg-white/30 text-white border-0"
                onClick={openPayAdmissionDialog}
              >
                Pay Now
              </Button>
            </div>
            <Building2 className="w-10 h-10 text-amber-200" />
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-6 text-white shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-red-100 text-sm">Pending Fees</p>
              <p className="text-3xl font-bold mt-2">₹{financeData.pendingFees.toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-2">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">From students</span>
              </div>
            </div>
            <Clock className="w-10 h-10 text-red-200" />
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Button 
          className="h-16 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 gap-2 text-lg"
          onClick={openCollectFeesDialog}
        >
          <CreditCard className="w-6 h-6" />
          Collect Fee
        </Button>
        <Button 
          className="h-16 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 gap-2 text-lg"
          onClick={() => setReceiptDialogOpen(true)}
        >
          <Receipt className="w-6 h-6" />
          Generate Receipt
        </Button>
        <Button 
          className="h-16 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 gap-2 text-lg" 
          onClick={openPayAdmissionDialog}
        >
          <IndianRupee className="w-6 h-6" />
          Pay Admission Fees
        </Button>
      </div>
      
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'overview' 
              ? 'bg-purple-100 text-purple-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4" />
            Overview
          </div>
        </button>
        <button
          onClick={() => setActiveTab('stt')}
          className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'stt' 
              ? 'bg-emerald-100 text-emerald-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            STT
            {installmentData.summary?.inst_1_pending_count > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {installmentData.summary.inst_1_pending_count + (installmentData.summary.inst_2_pending_count || 0)}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('rpl')}
          className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'rpl' 
              ? 'bg-amber-100 text-amber-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            RPL
            {rplData.summary?.total_students > 0 && (
              <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {rplData.summary.total_students}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('distance')}
          className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'distance' 
              ? 'bg-blue-100 text-blue-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Distance Ed.
            {distanceData.summary?.total_students > 0 && (
              <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                {distanceData.summary.total_students}
              </span>
            )}
          </div>
        </button>
      </div>
      
      {activeTab === 'overview' && (
        <>
          {/* Recent Transactions */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-slate-100">
              <h3 className="font-semibold text-slate-900">Recent Transactions</h3>
            </div>
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {financeData.recentTransactions.length > 0 ? (
                  financeData.recentTransactions.map((txn) => (
                    <tr key={txn.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{txn.type}</td>
                      <td className="px-4 py-3 text-slate-600">{txn.student}</td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-emerald-600">₹{txn.amount?.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{txn.date}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          txn.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {txn.status === 'completed' ? 'Completed' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                      No transactions yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      
      {activeTab === 'stt' && (
        /* STT Installment Tracking */
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">STT Students</p>
                  <p className="text-xl font-bold text-slate-900">{installmentData.summary?.total_students || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">2 Installment</p>
                  <p className="text-xl font-bold text-slate-900">{installmentData.summary?.two_installment_students || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">1st Pending</p>
                  <p className="text-xl font-bold text-amber-600">{installmentData.summary?.inst_1_pending_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">2nd Pending</p>
                  <p className="text-xl font-bold text-red-600">{installmentData.summary?.inst_2_pending_count || 0}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4">
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search student, course..." value={installmentSearch} onChange={(e) => setInstallmentSearch(e.target.value)} className="pl-10" />
            </div>
            <Button onClick={fetchInstallmentData} variant="outline" className="gap-2"><RefreshCw className="w-4 h-4" />Refresh</Button>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-emerald-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-600" />STT - Installment Tracking (60/40)</h3>
            </div>
            
            {loadingInstallments ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
            ) : filteredInstallmentStudents.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No STT students found</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">1st Inst. (60%)</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">2nd Inst. (40%)</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredInstallmentStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                              <span className="text-emerald-600 font-medium text-sm">{student.name?.charAt(0) || 'S'}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">{student.name}</p>
                              <p className="text-xs text-slate-500">{student.phone || student.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-sm text-slate-700">{student.course_name}</span></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">₹{(student.installment_1?.amount || 0).toLocaleString()}</span>
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${student.installment_1?.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {student.installment_1?.status === 'paid' ? '✓' : 'Pending'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {student.is_two_installment ? (
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900">₹{(student.installment_2?.amount || 0).toLocaleString()}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${student.installment_2?.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {student.installment_2?.status === 'paid' ? '✓' : 'Pending'}
                              </span>
                            </div>
                          ) : <span className="text-xs text-slate-400">N/A</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-emerald-600">₹{(student.fee_paid || 0).toLocaleString()}</span>
                          <span className="text-slate-400 mx-1">/</span>
                          <span className="text-sm text-slate-600">₹{(student.total_fee || 0).toLocaleString()}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'rpl' && (
        /* RPL Tracking */
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-amber-600" /></div>
                <div><p className="text-sm text-slate-500">RPL Students</p><p className="text-xl font-bold text-slate-900">{rplData.summary?.total_students || 0}</p></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><IndianRupee className="w-5 h-5 text-emerald-600" /></div>
                <div><p className="text-sm text-slate-500">Fee Collected</p><p className="text-xl font-bold text-emerald-600">₹{((rplData.summary?.fee_collected || 0) / 1000).toFixed(1)}K</p></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-red-600" /></div>
                <div><p className="text-sm text-slate-500">Fee Pending</p><p className="text-xl font-bold text-red-600">₹{((rplData.summary?.fee_pending || 0) / 1000).toFixed(1)}K</p></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Check className="w-5 h-5 text-blue-600" /></div>
                <div><p className="text-sm text-slate-500">Assessed</p><p className="text-xl font-bold text-blue-600">{rplData.summary?.assessed_count || 0}</p></div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4">
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search student, course..." value={rplSearch} onChange={(e) => setRplSearch(e.target.value)} className="pl-10" />
            </div>
            <Button onClick={fetchRplData} variant="outline" className="gap-2"><RefreshCw className="w-4 h-4" />Refresh</Button>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-amber-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Award className="w-5 h-5 text-amber-600" />RPL - Fee Tracking</h3>
            </div>
            
            {loadingRpl ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
            ) : filteredRplStudents.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><Award className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No RPL students found</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Total Fee</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Paid</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Pending</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Assessment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredRplStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                              <span className="text-amber-600 font-medium text-sm">{student.name?.charAt(0) || 'S'}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">{student.name}</p>
                              <p className="text-xs text-slate-500">{student.phone || student.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-sm text-slate-700">{student.course_name}</span></td>
                        <td className="px-4 py-3"><span className="font-semibold text-slate-900">₹{(student.total_fee || 0).toLocaleString()}</span></td>
                        <td className="px-4 py-3"><span className="font-semibold text-emerald-600">₹{(student.fee_paid || 0).toLocaleString()}</span></td>
                        <td className="px-4 py-3"><span className="font-semibold text-red-600">₹{(student.fee_pending || 0).toLocaleString()}</span></td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${student.assessment_status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {student.assessment_status === 'completed' ? 'Done' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'distance' && (
        /* Distance Education Tracking */
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
                <div><p className="text-sm text-slate-500">Total Students</p><p className="text-xl font-bold text-slate-900">{distanceData.summary?.total_students || 0}</p></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><IndianRupee className="w-5 h-5 text-emerald-600" /></div>
                <div><p className="text-sm text-slate-500">Fee Collected</p><p className="text-xl font-bold text-emerald-600">₹{((distanceData.summary?.fee_collected || 0) / 1000).toFixed(1)}K</p></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><GraduationCap className="w-5 h-5 text-purple-600" /></div>
                <div><p className="text-sm text-slate-500">Degree</p><p className="text-xl font-bold text-purple-600">{distanceData.summary?.degree_count || 0}</p></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center"><BookOpen className="w-5 h-5 text-sky-600" /></div>
                <div><p className="text-sm text-slate-500">Diploma</p><p className="text-xl font-bold text-sky-600">{distanceData.summary?.diploma_count || 0}</p></div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4">
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search student, course..." value={distanceSearch} onChange={(e) => setDistanceSearch(e.target.value)} className="pl-10" />
            </div>
            <Button onClick={fetchDistanceData} variant="outline" className="gap-2"><RefreshCw className="w-4 h-4" />Refresh</Button>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-blue-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2"><BookOpen className="w-5 h-5 text-blue-600" />Distance Education - Yearly Fee Tracking</h3>
            </div>
            
            {loadingDistance ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
            ) : filteredDistanceStudents.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No Distance Education students found</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Duration</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Yearly Fee</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Paid / Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredDistanceStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                              <span className="text-blue-600 font-medium text-sm">{student.name?.charAt(0) || 'S'}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">{student.name}</p>
                              <p className="text-xs text-slate-500">{student.phone || student.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-sm text-slate-700">{student.course_name}</span></td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${student.course_category === 'degree' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>
                            {student.course_category === 'degree' ? 'Degree' : 'Diploma'}
                          </span>
                        </td>
                        <td className="px-4 py-3"><span className="text-sm text-slate-700">{student.duration_years || 1} Years</span></td>
                        <td className="px-4 py-3"><span className="font-semibold text-slate-900">₹{(student.yearly_fee || 0).toLocaleString()}/yr</span></td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-emerald-600">₹{(student.fee_paid || 0).toLocaleString()}</span>
                          <span className="text-slate-400 mx-1">/</span>
                          <span className="text-sm text-slate-600">₹{(student.total_fee || 0).toLocaleString()}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Collect Fees Dialog */}
      <Dialog open={collectFeesDialogOpen} onOpenChange={setCollectFeesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Collect Fees / फीस जमा करें
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {!selectedStudent ? (
              <>
                {/* Students with Pending Fees */}
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Select a student to collect their pending fees.
                  </p>
                </div>
                
                {loadingStudents ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : pendingFeeStudents.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <p className="text-slate-600">All fees collected!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">{pendingFeeStudents.length} students with pending fees</p>
                    {pendingFeeStudents.map((student) => (
                      <div 
                        key={student.id}
                        className="bg-white rounded-xl border-2 border-slate-200 p-4 hover:border-emerald-300 cursor-pointer transition-all"
                        onClick={() => selectStudentForCollection(student)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold">
                              {student.name?.charAt(0)}
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900">{student.name}</h4>
                              <p className="text-sm text-slate-500">{student.course_name || student.course}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Pending Fee</p>
                            <p className="text-xl font-bold text-red-600">₹{student.fee_pending?.toLocaleString()}</p>
                            <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700">
                              Collect
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Fee Collection Form - Based on Enrollment */}
                <div className="bg-gradient-to-br from-purple-50 via-white to-blue-50 rounded-xl p-4 border border-purple-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-lg">
                      {selectedStudent.name?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{selectedStudent.name}</h4>
                      <p className="text-sm text-slate-600">{selectedStudent.course_name || selectedStudent.course}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedStudent(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Enrollment context chips */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-3 text-[11px]">
                    <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Based on Enrollment
                    </span>
                    {selectedStudent.training_mode && (
                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold uppercase">
                        {selectedStudent.training_mode}
                      </span>
                    )}
                    {selectedStudent.stt_delivery_mode && (
                      <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold capitalize">
                        {selectedStudent.stt_delivery_mode.replace('_', ' ')}
                      </span>
                    )}
                    {selectedStudent.fee_installment && (
                      <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
                        {selectedStudent.fee_installment === 'two' ? '2 Installments (60/40)' : 'Full Payment'}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white rounded-lg p-2.5 border border-slate-100">
                      <p className="text-[10px] text-slate-500 uppercase font-semibold">Total Fee</p>
                      <p className="font-bold text-slate-900">₹{(selectedStudent.total_fee || selectedStudent.course_fee)?.toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 border border-emerald-100">
                      <p className="text-[10px] text-emerald-600 uppercase font-semibold">Paid</p>
                      <p className="font-bold text-emerald-600">₹{(selectedStudent.fee_paid || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 border border-red-100">
                      <p className="text-[10px] text-red-600 uppercase font-semibold">Pending</p>
                      <p className="font-bold text-red-600">₹{selectedStudent.fee_pending?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Installment Breakdown (locked, from enrollment) */}
                {(selectedStudent.installment_1_amount > 0 || selectedStudent.installment_2_amount > 0) && (
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5 text-purple-600" /> Installment Breakdown
                      <span className="text-[10px] font-normal text-slate-400 normal-case">(locked from enrollment)</span>
                    </Label>
                    {[1, 2].map((n) => {
                      const amt = Number(selectedStudent[`installment_${n}_amount`]) || 0;
                      const paid = Number(selectedStudent[`installment_${n}_paid_amount`]) || 0;
                      const status = selectedStudent[`installment_${n}_status`] || 'pending';
                      const remaining = Math.max(0, amt - paid);
                      if (amt <= 0) return null;
                      const statusStyle = {
                        paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                        partial: 'bg-amber-100 text-amber-700 border-amber-200',
                        pending: 'bg-rose-100 text-rose-700 border-rose-200',
                        na: 'bg-slate-100 text-slate-500 border-slate-200',
                      }[status] || 'bg-slate-100 text-slate-700 border-slate-200';
                      return (
                        <div key={n} className={`border rounded-xl p-3 ${status === 'paid' ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-slate-200'}`} data-testid={`installment-${n}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${status === 'paid' ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                                {n}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{n === 1 ? '1st Installment' : '2nd Installment'} {selectedStudent.fee_installment === 'two' ? `(${n === 1 ? '60' : '40'}%)` : ''}</p>
                                <p className="text-[10px] text-slate-500">Due: {selectedStudent[`installment_${n}_due_date`] ? new Date(selectedStudent[`installment_${n}_due_date`]).toLocaleDateString('en-IN', {day:'2-digit', month:'short', year:'numeric'}) : '—'}</p>
                              </div>
                            </div>
                            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${statusStyle}`}>{status}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-xs text-slate-600">
                              <span className="font-bold text-slate-900">₹{amt.toLocaleString()}</span>
                              <span className="text-slate-400"> · Paid: ₹{paid.toLocaleString()} · Remaining: <span className="text-rose-600 font-semibold">₹{remaining.toLocaleString()}</span></span>
                            </div>
                            {remaining > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                                onClick={() => setCollectAmount(remaining.toString())}
                                data-testid={`pay-installment-${n}-btn`}
                              >
                                Pay ₹{remaining.toLocaleString()}
                              </Button>
                            )}
                          </div>
                          {amt > 0 && (
                            <div className="mt-2 w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${status === 'paid' ? 'bg-emerald-500' : status === 'partial' ? 'bg-amber-500' : 'bg-slate-300'}`} style={{ width: `${Math.min(100, (paid / amt) * 100)}%` }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <div>
                  <Label>Amount to Collect / जमा राशि *</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">₹</span>
                    <Input
                      type="number"
                      value={collectAmount}
                      onChange={(e) => setCollectAmount(e.target.value)}
                      max={selectedStudent.fee_pending}
                      className="pl-8 text-lg"
                      data-testid="collect-amount-input"
                    />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Click an installment "Pay" button above to auto-fill, or enter amount manually</p>
                </div>
                
                {/* Payment Mode Dropdown */}
                <div>
                  <Label>Payment Mode / भुगतान माध्यम *</Label>
                  <select
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                  >
                    <option value="Cash">💵 Cash / नकद</option>
                    <option value="UPI">📱 UPI (GPay, PhonePe, Paytm)</option>
                    <option value="Net Banking">🏦 Net Banking / नेट बैंकिंग</option>
                    <option value="Card">💳 Debit/Credit Card / कार्ड</option>
                  </select>
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setSelectedStudent(null)}>
                    Back
                  </Button>
                  <Button 
                    onClick={handleCollectFees}
                    disabled={isCollecting || !collectAmount || parseFloat(collectAmount) <= 0}
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 gap-2"
                  >
                    {isCollecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    Collect ₹{parseFloat(collectAmount || 0).toLocaleString()}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog - Same Format as Students Page */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="max-w-xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              Payment Receipt / भुगतान रसीद
            </DialogTitle>
          </DialogHeader>
          
          {receiptData ? (
            <div className="space-y-4">
              {/* Standard Receipt Preview with WYSIWYG Layout */}
              <div ref={receiptRef} id="print-receipt-finance">
                <div style={{ 
                  width: '100%', 
                  maxWidth: '420px', 
                  fontFamily: 'Inter, Arial, sans-serif',
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  margin: '0 auto',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                }}>
                  
                  {/* Header: Logo Left + Center Name */}
                  <div style={{ 
                    background: 'linear-gradient(to right, #059669, #0d9488, #0891b2)',
                    color: 'white',
                    padding: '16px',
                    position: 'relative',
                    overflow: 'hidden',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}>
                    {/* Background Pattern */}
                    <div style={{ 
                      position: 'absolute', 
                      inset: 0, 
                      opacity: 0.1,
                      backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                      backgroundSize: '10px 10px'
                    }}></div>
                    
                    <div style={{ position: 'relative', zIndex: 10 }}>
                      {/* Logo Left + Center Name Center */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        {/* Logo - Left Side - BIGGER */}
                        <img 
                          src="/iqraskills-logo.png" 
                          alt="Logo" 
                          style={{ 
                            width: '80px', 
                            height: '80px', 
                            borderRadius: '10px',
                            backgroundColor: 'white',
                            padding: '6px',
                            objectFit: 'contain',
                            flexShrink: 0
                          }} 
                        />
                        
                        {/* Center Name & Address - Center */}
                        <div style={{ flex: 1, textAlign: 'center' }}>
                          <h1 style={{ fontSize: '20px', fontWeight: '800', letterSpacing: '0.05em', margin: 0 }}>
                            {centerInfo?.center_name || 'Training Center'}
                          </h1>
                          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', marginTop: '4px' }}>
                            {centerInfo?.address ? `${centerInfo.address}, ${centerInfo.city || ''}, ${centerInfo.district || ''}` : 'Center Address'}
                          </p>
                          <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', marginTop: '2px' }}>
                            Code: {centerInfo?.center_code || 'IQRA-XX-00000'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Receipt Title Badge */}
                      <div style={{ marginTop: '12px', textAlign: 'center' }}>
                        <div style={{ 
                          display: 'inline-block',
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          padding: '8px 16px'
                        }}>
                          <p style={{ fontSize: '14px', fontWeight: 'bold', letterSpacing: '0.1em', margin: 0 }}>PAYMENT RECEIPT</p>
                          <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', margin: '2px 0 0' }}>भुगतान रसीद</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info Banner */}
                  <div style={{ 
                    backgroundColor: '#ecfdf5',
                    borderBottom: '1px solid #d1fae5',
                    padding: '8px 16px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '24px',
                    fontSize: '12px',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Phone className="w-3 h-3 text-emerald-600" />
                      <span style={{ color: '#047857', fontWeight: '500' }}>{centerInfo?.mobile || centerInfo?.phone || 'Contact'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Mail className="w-3 h-3 text-emerald-600" />
                      <span style={{ color: '#047857', fontWeight: '500' }}>{centerInfo?.email || 'Email'}</span>
                    </div>
                  </div>

                  {/* Receipt Details */}
                  <div className="p-4 space-y-4">
                    
                    {/* Receipt Info Row */}
                    <div className="flex justify-between text-sm border-b border-dashed border-slate-300 pb-3">
                      <div>
                        <p className="text-slate-400 text-xs">Receipt No.</p>
                        <p className="font-bold font-mono text-slate-800">{receiptData.receiptNo}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-xs">Date</p>
                        <p className="font-semibold text-slate-800">{receiptData.date}</p>
                      </div>
                    </div>

                    {/* Student Info */}
                    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-slate-400 text-xs uppercase">Student Name</p>
                          <p className="font-bold text-slate-800">{receiptData.studentName}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs uppercase">Student ID</p>
                          <p className="font-mono font-semibold text-emerald-700">{receiptData.studentId || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs uppercase">Mobile</p>
                          <p className="font-medium text-slate-700">{receiptData.phone || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs uppercase">Course</p>
                          <p className="font-medium text-sm text-slate-700">{receiptData.course || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Table */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left px-3 py-2 text-slate-600 font-semibold">Description</th>
                            <th className="text-right px-3 py-2 text-slate-600 font-semibold w-28">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <p className="font-medium text-slate-700">Total Course Fee</p>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-700">
                              {(receiptData.totalFee || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-t border-slate-100 bg-emerald-50">
                            <td className="px-3 py-2 text-emerald-700">
                              <p className="font-medium">Total Paid</p>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                              {(receiptData.totalPaid || 0).toLocaleString()}
                            </td>
                          </tr>
                          {receiptData.pendingAfter > 0 && (
                            <tr className="bg-rose-50 border-t border-slate-100">
                              <td className="px-3 py-2 text-rose-700">
                                <p className="font-medium">Remaining Balance</p>
                              </td>
                              <td className="px-3 py-2 text-right font-semibold text-rose-700">
                                {(receiptData.pendingAfter || 0).toLocaleString()}
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                            <td className="px-3 py-3 font-bold text-base">
                              AMOUNT PAID
                            </td>
                            <td className="px-3 py-3 text-right font-bold text-xl">
                              ₹{(receiptData.amountPaid || 0).toLocaleString()}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Payment Status Badge - only show PAID IN FULL */}
                    {receiptData.pendingAfter <= 0 && (
                      <div className="text-center">
                        <span className="inline-block px-4 py-2 rounded-lg border-2 font-bold text-sm uppercase tracking-wider border-emerald-500 text-emerald-600 bg-emerald-50">
                          ✓ PAID IN FULL
                        </span>
                      </div>
                    )}

                    {/* Payment Mode */}
                    <div className="flex justify-between text-sm border-t border-dashed border-slate-300 pt-3">
                      <div>
                        <p className="text-slate-400 text-xs uppercase">Payment Mode</p>
                        <p className="font-semibold text-slate-700">{receiptData.paymentMode || 'Cash'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-xs uppercase">Transaction ID</p>
                        <p className="font-mono text-xs text-slate-600">TXN{Date.now().toString().slice(-10)}</p>
                      </div>
                    </div>

                    {/* Signatures */}
                    <div className="flex justify-between pt-4 mt-4 border-t border-slate-200">
                      <div className="text-center">
                        <div className="w-24 h-10 border-b-2 border-slate-400 mb-1"></div>
                        <p className="text-xs text-slate-600">Student Sign</p>
                      </div>
                      <div className="text-center">
                        <div className="w-24 h-10 border-b-2 border-slate-400 mb-1"></div>
                        <p className="text-xs text-slate-600">Authorized Sign</p>
                      </div>
                    </div>

                    {/* Footer Note */}
                    <div className="text-center pt-3 border-t border-slate-100">
                      <p className="text-[9px] text-slate-400">
                        * This is a computer generated receipt.
                      </p>
                      <p className="text-[9px] text-slate-400">
                        यह एक कंप्यूटर जनित रसीद है।
                      </p>
                    </div>
                  </div>
                  
                  {/* Bottom Gradient Strip */}
                  <div className="h-1.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600"></div>
                </div>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Print Size: A5 (148mm × 210mm)
              </p>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setReceiptDialogOpen(false); setReceiptData(null); }} className="gap-2">
                  <X className="w-4 h-4" />
                  Close
                </Button>
                <Button
                  onClick={() => handleSendReceiptWhatsApp(receiptData)}
                  className="bg-[#25D366] hover:bg-[#1ea855] text-white gap-2 shadow-md shadow-emerald-500/20"
                  data-testid="send-whatsapp-receipt-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  Send on WhatsApp
                </Button>
                <Button 
                  onClick={handlePrintReceipt} 
                  className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 gap-2 shadow-lg shadow-emerald-500/25"
                >
                  <Printer className="w-4 h-4" />
                  Print Receipt
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Collect fees first to generate receipt</p>
              <Button className="mt-4" onClick={() => { setReceiptDialogOpen(false); openCollectFeesDialog(); }}>
                Collect Fees
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pay Admission Fees Dialog */}
      <Dialog open={payAdmissionDialogOpen} onOpenChange={setPayAdmissionDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-amber-600" />
              Pay Admission Fees / एडमिशन फीस भुगतान
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Select a student to pay their admission fees (IQRA's share).
                Amount is fixed based on course type.
              </p>
            </div>
            
            {loadingStudents ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : admissionPendingStudents.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-slate-600">All admission fees paid!</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm text-slate-600 pb-2 border-b">
                  <span>{admissionPendingStudents.length} students with pending admission fees</span>
                  <span className="font-semibold text-amber-700">
                    Total: ₹{admissionPendingStudents.reduce((sum, s) => sum + (s.iqra_share_pending || 0), 0).toLocaleString()}
                  </span>
                </div>
                
                {admissionPendingStudents.map((student) => (
                  <div 
                    key={student.id}
                    className={`bg-white rounded-xl border-2 p-4 transition-all ${
                      selectedStudent?.id === student.id 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-slate-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold">
                            {student.name?.charAt(0)}
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{student.name}</h4>
                            <p className="text-sm text-slate-500">{student.student_id}</p>
                          </div>
                        </div>
                        
                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-1 text-slate-600">
                            <GraduationCap className="w-4 h-4" />
                            <span>{student.course_name}</span>
                          </div>
                          <div className="flex items-center gap-1 text-slate-600">
                            <Users className="w-4 h-4" />
                            <span className={student.course_category === 'professional' ? 'text-emerald-600' : 'text-blue-600'}>
                              {student.course_category === 'professional' ? 'Professional' : 'Degree/Diploma'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="mb-2">
                          <p className="text-xs text-slate-500">Total Fee: ₹{student.total_fee?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">Share: {student.iqra_share_percent}%</p>
                        </div>
                        <div className="bg-amber-100 rounded-lg px-3 py-2 mb-2">
                          <p className="text-xs text-amber-600">Admission Fees</p>
                          <p className="text-xl font-bold text-amber-700">₹{student.iqra_share_pending?.toLocaleString()}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handlePayAdmissionFees(student)}
                          disabled={isProcessing && selectedStudent?.id === student.id}
                          className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800"
                        >
                          {isProcessing && selectedStudent?.id === student.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <IndianRupee className="w-4 h-4 mr-1" />
                              Pay ₹{student.iqra_share_pending?.toLocaleString()}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 mt-4">
              <p className="text-xs text-slate-600 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Payment via Razorpay (UPI, Cards, NetBanking)
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterFinancePage;
